import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';


const logger = createLogger('IndexRedirect');

/**
 * Página inicial que redireciona automaticamente baseado no estado de autenticação.
 *
 * Regras de roteamento:
 *  - Não autenticado            → /auth
 *  - Root admin                 → /admin
 *  - Convite pendente / órfão   → /pending
 *  - Cruzamento WL × tenant     → /select-context (ou direto se houver escolha
 *                                  prévia em localStorage)
 *  - Membro de tenant (sem WL)  → /tenant (ou /tenant/chat se agente)
 *  - Operador WL sem tenant     → /wl
 *  - Sem nada                   → /onboarding
 *
 * CHANGELOG:
 *   v1.5.0 — 2026-05-29 — Remove o atalho via localStorage no ramo de
 *            cruzamento. Toda vez que o usuário for WL + membro de
 *            qualquer tenant, vai para /select-context. A "memória de
 *            escolha" só vale para o switcher do header (dentro da
 *            sessão), não para o boot pós-login.
 *   v1.4.0 — 2026-05-29 — Roteamento por cruzamento real WL × tenant_members
 *            (independente do tenant.kind).
 *   v1.3.0 — Suporte a usuários que são operadores WL E também membros do tenant
 *            interno (kind='wl_internal').
 *   v1.2.0 — Adiciona check `is_wl_user` ANTES do fallback de onboarding.
 *   v1.1.0 — Agentes vão direto para /tenant/chat.
 */
const Index = () => {
  const {
    user,
    isRootAdmin,
    isTenantMember,
    isAgent,
    hasPendingInvite,
    roles,
    loading,
    dataLoaded,
  } = useAuth();
  const navigate = useNavigate();
  // Evita múltiplas chamadas do RPC is_wl_user em re-renders sucessivos
  const wlCheckInFlight = useRef(false);

  useEffect(() => {
    if (loading || !dataLoaded) return;

    // Usuário não autenticado
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }

    // 1. Root admin
    if (isRootAdmin) {
      navigate('/admin', { replace: true });
      return;
    }

    // 2. Convite pendente — antes de qualquer outra coisa para não confundir UX
    if (hasPendingInvite) {
      navigate('/pending', { replace: true });
      return;
    }

    // 3. Cruzamento WL × tenant
    //    Se o usuário tem QUALQUER tenant_members ativo (independente do kind)
    //    E também é operador WL, oferece a tela de escolha. Caso já tenha
    //    escolhido antes (localStorage), vai direto para o painel escolhido.
    //    Caso só seja WL (sem tenant), vai direto pro /wl.
    if (wlCheckInFlight.current) return;
    wlCheckInFlight.current = true;

    supabase
      .rpc('is_wl_user', { _user_id: user.id })
      .then(({ data, error }) => {
        const isWl = !error && data === true;

        // 3a. Cruzamento real: WL + membership em algum tenant
        //     Sempre apresenta a tela de escolha no login. A "memória de
        //     escolha" só é usada para o switcher do header (dentro da sessão).
        if (isWl && isTenantMember) {
          navigate('/select-context', { replace: true });
          return;
        }

        // 3b. Operador WL puro (sem nenhum tenant)
        if (isWl) {
          navigate('/wl', { replace: true });
          return;
        }

        // 3c. Membro de tenant puro (sem WL)
        if (isTenantMember) {
          navigate(isAgent ? '/tenant/chat' : '/tenant', { replace: true });
          return;
        }

        // 4. Role órfã (sem membership efetivo) → aguarda admin
        const hasAnyTenantRole = roles.some((r) => r.tenant_id !== null);
        if (hasAnyTenantRole) {
          navigate('/pending', { replace: true });
          return;
        }

        // 5. Sem nada → onboarding
        if (error) {
          logger.warn('Falha ao verificar is_wl_user; seguindo para onboarding', error);
        }
        navigate('/onboarding', { replace: true });
      });
  }, [
    user,
    isRootAdmin,
    isTenantMember,
    isAgent,
    hasPendingInvite,
    roles,
    loading,
    dataLoaded,
    navigate,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
};

export default Index;
